import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { NO_DEPARTMENT } from '../../../constants/organizations';
import { selectFilteredDepartmentListForUser } from '../../../store/selectors/organizations';

interface IProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
  helperText?: string;
  tooltip?: string;
}

export const DepartmentSelect = ({ value, onChange, helperText, disabled, tooltip }: IProps) => {
  const departments = useSelector(selectFilteredDepartmentListForUser);

  const visibleDepartments = useMemo(() => {
    const filtered = (departments ?? []).filter((dep) => dep?.uid !== NO_DEPARTMENT);
    if (filtered.length === 0) {
      return departments ?? [];
    }
    return filtered;
  }, [departments]);

  // Check if value is effectively empty (either empty array or only contains NO_DEPARTMENT)
  const hasActualSelection = useMemo(() => {
    return value.length > 0 && value.some((id) => id !== NO_DEPARTMENT);
  }, [value]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const selectedValue = event.target.value;
      onChange(typeof selectedValue === 'string' ? selectedValue.split(',') : selectedValue);
    },
    [onChange],
  );

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onChange([]);
    },
    [onChange],
  );

  const renderDepartmentsValue = useCallback(
    (selected: string[]) => {
      const actualSelected = selected.filter((id) => id !== NO_DEPARTMENT);

      if (actualSelected.length === 0) {
        return '';
      }

      return (visibleDepartments ?? [])
        .filter((dep) => dep?.uid && actualSelected.includes(dep.uid))
        .map((dep) => dep?.name)
        .join(', ');
    },
    [visibleDepartments],
  );

  return (
    <>
      <Tooltip title={tooltip || ''} disableHoverListener={!tooltip} arrow>
        <FormControl fullWidth variant="outlined">
          <InputLabel
            sx={{
              '&.MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 9px) scale(1)',
              },
            }}
          >
            Department
          </InputLabel>
          <Select
            multiple
            size="small"
            disabled={disabled}
            value={hasActualSelection ? value : []}
            onChange={handleChange}
            label="Department"
            renderValue={renderDepartmentsValue}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    maxHeight: 250,
                    maxWidth: 300,
                  },
                },
              },
            }}
            endAdornment={
              hasActualSelection ? (
                <InputAdornment position="end" sx={{ mr: 3 }}>
                  <IconButton size="small" onClick={handleClear} edge="end" sx={{ padding: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          >
            {visibleDepartments?.map(({ uid, name }) => {
              const isChecked = Boolean(uid) && uid !== NO_DEPARTMENT && value.includes(uid);

              return (
                <MenuItem key={uid} value={uid}>
                  <Checkbox checked={isChecked} />
                  <ListItemText primary={name} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Tooltip>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 0.5,
            ml: 1.75,
          }}
        >
          {helperText}
        </Typography>
      )}
    </>
  );
};
